import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';

/**
 * Temporary homepage announcement.
 * Times are US Eastern (America/New_York): on 2026-04-14 the offset is EDT (UTC−4).
 */
const MODAL_VISIBLE_UNTIL_MS = Date.parse('2026-04-14T16:00:00.000Z'); // Apr 14, 2026 12:00 PM Eastern
const HIDE_PREFERENCE_CUTOFF_MS = Date.parse('2026-04-14T14:30:00.000Z'); // Apr 14, 2026 10:30 AM Eastern

const LS_KEY = 'qgold_pea_webinar_announcement_dismiss_v1';

/** PEA press release (slug from Strapi `unique` on the news entry). */
const PRESS_RELEASE_URL =
    '/news/q-gold-resources-provides-corporate-update-announces-preliminary-economic-assessment-pea-timeline-and-management-transition-to-advance-quartz-mountain-gold-project';
const WEBINAR_URL = 'https://us06web.zoom.us/meeting/register/0710C4SnTUWTxff5soUKkg';

function readDismissState() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || typeof data !== 'object') return null;
        return data;
    } catch {
        return null;
    }
}

function shouldSuppressModal(now) {
    if (now >= MODAL_VISIBLE_UNTIL_MS) return true;

    const data = readDismissState();
    if (!data) return false;

    if (data.permanent === true) return true;

    if (typeof data.hideUntilMs === 'number') {
        if (now < data.hideUntilMs) return true;
        localStorage.removeItem(LS_KEY);
        return false;
    }

    return false;
}

function PeaAnnouncementModal() {
    const [show, setShow] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    useEffect(() => {
        if (Date.now() >= MODAL_VISIBLE_UNTIL_MS) return;
        if (shouldSuppressModal(Date.now())) return;

        // Delay the modal slightly so it doesn't instantly block the user on load
        const timer = setTimeout(() => {
            setShow(true);
        }, 2800);

        return () => clearTimeout(timer);
    }, []);

    const persistDismiss = useCallback(() => {
        const now = Date.now();
        if (!dontShowAgain) return;

        if (now >= HIDE_PREFERENCE_CUTOFF_MS) {
            localStorage.setItem(LS_KEY, JSON.stringify({ permanent: true }));
            return;
        }

        localStorage.setItem(
            LS_KEY,
            JSON.stringify({ hideUntilMs: HIDE_PREFERENCE_CUTOFF_MS })
        );
    }, [dontShowAgain]);

    const handleClose = useCallback(() => {
        persistDismiss();
        setShow(false);
    }, [persistDismiss]);

    if (Date.now() >= MODAL_VISIBLE_UNTIL_MS) return null;

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            className="pea-announcement-modal"
            dialogClassName="pea-announcement-modal__dialog"
        >
            <Modal.Header closeButton closeVariant="white" className="pea-announcement-modal__header">
                <Modal.Title as="h2" className="h5 mb-0 pea-announcement-modal__title">
                    Q-Gold Announces PEA Results - Shareholder Webinar April 14
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pea-announcement-modal__body">
                <p className="pea-announcement-modal__lead mb-3">
                    Q-Gold has released the Preliminary Economic Assessment (PEA) for the Q-Gold Project.
                    Management will discuss the results and outlook during a shareholder webinar on Tuesday,
                    April 14.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-2 gap-sm-3 mb-4">
                    {PRESS_RELEASE_URL ? (
                        <Button
                            variant="warning"
                            className="pea-announcement-modal__cta-primary text-white fw-semibold"
                            href={PRESS_RELEASE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            as="a"
                        >
                            Read the Press Release
                        </Button>
                    ) : (
                        <Button
                            variant="warning"
                            className="pea-announcement-modal__cta-primary text-white fw-semibold pea-announcement-modal__cta-pending"
                            disabled
                            title="QGold To Host Live Webinar"
                        >
                            Read the Press Release
                        </Button>
                    )}
                    {WEBINAR_URL ? (
                        <Button
                            variant="outline-light"
                            className="pea-announcement-modal__cta-secondary fw-semibold"
                            href={WEBINAR_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            as="a"
                        >
                            Register for the Webinar
                        </Button>
                    ) : (
                        <Button
                            variant="outline-light"
                            className="pea-announcement-modal__cta-secondary fw-semibold pea-announcement-modal__cta-pending"
                            disabled
                            title="Link coming soon"
                        >
                            Register for the Webinar
                        </Button>
                    )}
                </div>
                <Form.Check
                    type="checkbox"
                    id="pea-announcement-hide-future"
                    className="pea-announcement-modal__checkbox text-white-50 small"
                    label="Do not show this message again on future visits"
                    checked={dontShowAgain}
                    onChange={(e) => setDontShowAgain(e.target.checked)}
                />
            </Modal.Body>
        </Modal>
    );
}

export default PeaAnnouncementModal;
